'use client';
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Upload, X, Loader2, ImageIcon } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { secureImageUpload } from '@/lib/secureImageUpload';
import Image from 'next/image';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input-2024';

interface ImageUploadProps {
  onImageUploaded: (url: string) => void;
  onImageRemoved?: () => void;
  existingImageUrl?: string;
  className?: string;
  buttonVariant?: 'default' | 'secondary' | 'outline' | 'ghost';
  buttonSize?: 'default' | 'sm' | 'lg' | 'icon';
  buttonLabel?: string;
  showPreview?: boolean;
}

export function ImageUpload({
  onImageUploaded,
  onImageRemoved,
  existingImageUrl,
  className,
  showPreview = true
}: ImageUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingImageUrl || null);
  const [imageUrl, setImageUrl] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (file: File) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file',
        variant: 'destructive'
      });
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Please upload an image smaller than 10MB',
        variant: 'destructive'
      });
      return;
    }

    setIsUploading(true);

    try {
      const imageUrl = await secureImageUpload(file);

      if (!imageUrl) {
        throw new Error('Failed to upload image');
      }

      setPreviewUrl(imageUrl);
      onImageUploaded(imageUrl);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload image',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleInputFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileChange(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl) return;

    try {
      setIsUploading(true);
      // Validate URL
      new URL(imageUrl);

      // Use our proxy endpoint to check if URL points to an image
      const proxyUrl = `/api/image/proxy?url=${encodeURIComponent(imageUrl)}`;
      const response = await fetch(proxyUrl);

      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const contentType = response.headers.get('content-type');
      if (!contentType?.startsWith('image/')) {
        throw new Error('URL does not point to a valid image');
      }

      setPreviewUrl(imageUrl);
      onImageUploaded(imageUrl);
      setImageUrl('');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Please enter a valid image URL',
        variant: 'destructive'
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreviewUrl(null);
    setImageUrl('');
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const items = e.clipboardData.items;
    for (const item of items) {
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          handleFileChange(file);
          return;
        }
      }
    }
  };

  return (
    <div className={cn('space-y-4', className)}>
      <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleInputFileChange} />

      {showPreview && previewUrl ? (
        <div className="relative w-full aspect-square rounded-lg overflow-hidden bg-muted">
          <Image src={previewUrl} alt="Uploaded image" fill className="object-cover" />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 shadow-lg"
            onClick={handleRemove}
            disabled={isUploading}
          >
            <X className="h-4 w-4" />
          </Button>
          {isUploading && (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
                <p className="text-sm font-medium">Processing image...</p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          className={cn(
            'border-2 border-dashed rounded-lg p-6 transition-all duration-200 relative',
            isDragging ? 'border-primary bg-primary/5 scale-[1.02]' : 'border-border hover:border-muted-foreground',
            'flex flex-col items-center justify-center gap-4'
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {isUploading ? (
            <div className="absolute inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center rounded-lg z-10">
              <div className="flex flex-col items-center gap-3">
                <div className="relative">
                  <div className="inset-0 animate-bounce">
                    <ImageIcon className="h-8 w-8 text-primary/30" />
                  </div>
                </div>
                <p className="text-sm font-medium">Uploading image...</p>
                <p className="text-xs text-muted-foreground">Please wait while we process your image</p>
              </div>
            </div>
          ) : null}

          <div className="text-4xl text-muted-foreground transition-transform duration-200 group-hover:scale-110">
            <Upload />
          </div>
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-1">
              Drag an image here or{' '}
              <button
                type="button"
                className="text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                upload a file
              </button>
            </p>
            <p className="text-xs text-muted-foreground">PNG, JPG up to 10MB</p>
          </div>

          <div className="w-full max-w-sm">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">OR</span>
              </div>
            </div>
          </div>

          <div className="flex w-full max-w-sm items-center space-x-2 justify-center">
            <Input
              type="text"
              placeholder="Paste image link"
              value={imageUrl}
              onChange={e => setImageUrl(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleUrlSubmit();
                }
              }}
              disabled={isUploading}
              className="transition-colors focus-visible:ring-primary"
            />
            <Button
              type="button"
              disabled={!imageUrl || isUploading}
              onClick={handleUrlSubmit}
              className="min-w-[70px]"
            >
              {isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
